import { useState } from "react";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import toast from "components/ui/toast/Toast";
import { useUpdateBidResponses } from "lib/hooks/api/bid/bid_responses/useUpdateBidResponses";
import { pluralise } from "lib/utils";
import { BidResponseStageButton } from "./bid_response_stage/BidResponseStageButton";
import DeleteBidResponsesModal from "./modals/DeleteBidResponseModal";
import { BidResponseUserBulkUpdateButton } from "./BidResponseUserBulkUpdateButton";
import { ExportBidResponsesButton } from "./ExportBidResponsesButton";
import { BidResponsesFilters } from "./utils";

type Props = {
  selectedIds: string[];
  clearSelection: () => void;
  filters: BidResponsesFilters;
  bidName?: string;
};

function formatUserName(user: { firstName: string; lastName: string } | null | undefined): string {
  return user ? `${user.firstName} ${user.lastName}` : "Unassigned";
}

export default function BidResponseActionBar({
  selectedIds,
  clearSelection,
  filters,
  bidName,
}: Props) {
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const { mutate: bulkUpdate, isLoading } = useUpdateBidResponses({
    onSuccess: (data, variables) => {
      const count = data.updateBidResponses.length;
      if (count === 0) return;

      const label = pluralise(count, "bid response");
      const { input } = variables;
      const first = data.updateBidResponses[0];

      if (input.stageId !== undefined) {
        toast.success(`${label} changed to '${first.stage.name}' stage`);
      } else if (input.assigneeId !== undefined) {
        toast.success(`${label} assignee changed to '${formatUserName(first.assignee)}'`);
      } else if (input.reviewerId !== undefined) {
        toast.success(`${label} reviewer changed to '${formatUserName(first.reviewer)}'`);
      }
    },
    onError: () => {
      toast.error("Failed to update bid responses");
    },
  });

  return (
    <>
      <Container aria-label="Bid responses bulk action bar">
        <Flex gap={12} alignItems="center">
          <Text color="white">{`${selectedIds.length} selected:`}</Text>

          <ActionButtonWrapper onClick={() => setDeleteModalOpen(true)}>
            <Button iconProps={{ size: 16, color: "white" }} icon="trash01">
              Delete
            </Button>
          </ActionButtonWrapper>
          <ActionButtonWrapper>
            <ExportBidResponsesButton
              filters={{
                ids: selectedIds,
                orderBy: filters.orderBy,
                order: filters.order,
              }}
              totalCount={selectedIds.length}
              buttonIcon="download01"
              bidName={bidName}
            />
          </ActionButtonWrapper>
          <ActionButtonWrapper>
            <BidResponseStageButton
              buttonType="primary"
              loading={isLoading}
              buttonContent="Status"
              chevron={false}
              showCheckBox={false}
              onClick={(stageId) =>
                bulkUpdate({
                  input: {
                    ids: selectedIds,
                    stageId,
                  },
                })
              }
            />
          </ActionButtonWrapper>
          <ActionButtonWrapper>
            <BidResponseUserBulkUpdateButton
              label="Assignee"
              loading={isLoading}
              onClick={(userId) =>
                bulkUpdate({
                  input: {
                    ids: selectedIds,
                    assigneeId: userId,
                  },
                })
              }
            />
          </ActionButtonWrapper>
          <ActionButtonWrapper>
            <BidResponseUserBulkUpdateButton
              label="Reviewer"
              loading={isLoading}
              onClick={(userId) =>
                bulkUpdate({
                  input: {
                    ids: selectedIds,
                    reviewerId: userId,
                  },
                })
              }
            />
          </ActionButtonWrapper>
        </Flex>
        <Flex gap={8} alignItems="center">
          <Button aria-label="Close" icon="xClose" onClick={clearSelection} />
        </Flex>
      </Container>
      <DeleteBidResponsesModal
        isOpen={isDeleteModalOpen}
        ids={selectedIds}
        onOpenChange={(open) => {
          if (!open) {
            clearSelection();
          }
          setDeleteModalOpen(open);
        }}
      />
    </>
  );
}

const Container = styled(Flex)(({ theme }) => ({
  justifyContent: "space-between",
  alignItems: "center",
  height: 48,
  padding: 16,
  backgroundColor: theme.colors.sysPrimaryDefault,
  borderRadius: 4,
  boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.12)",
}));

const ActionButtonWrapper = styled.div(({ theme }) => ({
  border: `1px solid ${theme.colors.sysTextWhite}`,
  borderRadius: 4,
  position: "relative",
}));
